import { Anchor, Text, Title } from '@mantine/core';
import type { Format } from '@quizee/core';
import { Link } from 'react-router-dom';
import { FormatGlyph } from '../components/FormatGlyph';
import { FORMAT_CHOICES } from '../format';

const DESCRIPTIONS: Record<Format, string> = {
  poll: 'One question. Readers see the percentages as they vote.',
  trivia_quiz: 'Test their knowledge with questions, correct answers and a final score.',
  personality_quiz: 'Help every reader discover their own result.',
  survey: 'Mix question types to collect feedback, with no public results.',
  form: 'A home for tip-offs, competition entries and reader stories.',
};

function Spark() {
  return (
    <svg
      width="42"
      height="42"
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path d="M20 3v34M3 20h34M8 8l24 24M8 32 32 8" />
    </svg>
  );
}

function Arrow() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M6 18 18 6M6 6h12v12" />
    </svg>
  );
}

/** Small, static examples explain the formats before an editor commits to one. */
function FormatExample({ format }: { format: Format }) {
  return (
    <div className={`format-example format-example-${format}`} aria-hidden="true">
      <div className="example-sheet">
        <span className="example-label">
          {format === 'trivia_quiz' ? 'THE WEEKLY QUIZ' : 'EXAMPLE'}
        </span>
        {format === 'poll' && (
          <>
            <strong>What should we cover next?</strong>
            <span className="example-option">
              <i /> Life in our town
            </span>
            <span className="example-option example-selected">
              <i /> The stories behind the news{' '}
              <b>
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m3 8 3 3 7-7" />
                </svg>
              </b>
            </span>
          </>
        )}
        {format === 'trivia_quiz' && (
          <>
            <strong>How well do you know your city?</strong>
            <div className="example-progress">
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>
            <span className="example-caption">A little local knowledge goes a long way.</span>
          </>
        )}
        {format === 'personality_quiz' && (
          <>
            <span className="example-outcome">
              <Spark />
            </span>
            <strong>The weekend explorer</strong>
            <span className="example-caption">There is always somewhere new to go.</span>
          </>
        )}
        {format === 'survey' && (
          <>
            <strong>How connected do you feel to your town?</strong>
            <div className="example-scale">
              {[1, 2, 3, 4, 5].map((n) => (
                <span key={n}>{n}</span>
              ))}
            </div>
            <div className="example-scale-labels">
              <span>Not very</span>
              <span>Very</span>
            </div>
          </>
        )}
        {format === 'form' && (
          <>
            <strong>Every neighbourhood has a story.</strong>
            <span className="example-input">Your name</span>
            <span className="example-input">Tell us yours…</span>
          </>
        )}
      </div>
    </div>
  );
}

export function ChooseFormat() {
  return (
    <>
      <Anchor className="back-link" component={Link} to="/interactions" size="sm">
        <span aria-hidden="true">← </span>Back to interactions
      </Anchor>
      <div className="format-picker-heading">
        <Text className="page-eyebrow">MAKE SOMETHING WORTH ANSWERING</Text>
        <Title order={1}>New interaction</Title>
        <Text c="dimmed" mt="sm">
          Start with a format. Make it your own.
        </Text>
      </div>
      <ul className="format-picker-grid">
        {FORMAT_CHOICES.map((choice) => (
          <li key={choice.format}>
            <Anchor
              component={Link}
              to={`/interactions/new/${choice.format}`}
              aria-label={choice.label}
              className="format-picker-card"
              underline="never"
              c="inherit"
            >
              <FormatExample format={choice.format} />
              <div className="format-picker-copy">
                <div className="format-card-title">
                  <FormatGlyph format={choice.format} />
                  <Title order={2} size="h3">
                    {choice.label}
                  </Title>
                  <span aria-hidden="true">
                    <Arrow />
                  </span>
                </div>
                <Text size="sm" c="dimmed">
                  {DESCRIPTIONS[choice.format]}
                </Text>
              </div>
            </Anchor>
          </li>
        ))}
        <li>
          <Anchor
            component={Link}
            to="/interactions/new/draft"
            className="format-picker-card drafting-card"
            underline="never"
            c="inherit"
            aria-label="Build with magic"
          >
            <div className="drafting-art" aria-hidden="true">
              <span>
                <Spark />
              </span>
              <span className="drafting-line" />
              <span className="drafting-line" />
              <span className="drafting-line" />
            </div>
            <div className="format-picker-copy">
              <div className="format-card-title">
                <Title order={2} size="h3">
                  Build with magic
                </Title>
                <span aria-hidden="true">
                  <Arrow />
                </span>
              </div>
              <Text size="sm">
                Turn an article or an idea into a first draft. You choose the format, edit and
                approve it before publishing.
              </Text>
            </div>
          </Anchor>
        </li>
      </ul>
      <Text className="format-picker-footnote" c="dimmed" size="xs">
        Every format works in your articles and uses your brand’s theme.
      </Text>
    </>
  );
}
